import React, { useState } from 'react';
import {
  Box, Card, Chip, Collapse, IconButton, InputAdornment, Menu, MenuItem,
  TextField, Tooltip, Typography,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import SegmentIcon from '@mui/icons-material/Segment';
import NotesIcon from '@mui/icons-material/Notes';
import BuildIcon from '@mui/icons-material/Build';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import RichTextField from './RichTextField';

const formatEuro = (value) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value || 0);

const KIND_META = {
  service: { icon: <BuildIcon fontSize="small" />, label: 'Leistung' },
  heading: { icon: <SegmentIcon fontSize="small" />, label: 'Zwischenüberschrift' },
  text: { icon: <NotesIcon fontSize="small" />, label: 'Text' },
};

/**
 * Ein Baustein eines Longform-Angebots.
 *
 * Eingeklappt ist die Zeile bereits vollständig bedienbar -- Titel und Aufwand
 * sind Eingabefelder, kein Dialog dazwischen. Aufgeklappt kommt die Prosa dazu.
 */
function SortableBlockCard({
  item, number, disabled, onPatch, onDelete, onDuplicate, onSaveAsTemplate,
}) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: item.id, disabled });

  const [open, setOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [title, setTitle] = useState(item.description || '');
  const [days, setDays] = useState(item.quantity ?? '');

  const kind = item.kind || 'service';
  const isService = kind === 'service';
  const isHeading = kind === 'heading';
  const meta = KIND_META[kind] || KIND_META.service;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const closeMenu = () => setMenuAnchor(null);

  return (
    <Card
      ref={setNodeRef}
      style={style}
      variant="outlined"
      sx={{
        mb: 1,
        bgcolor: isHeading ? 'grey.100' : 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
        <Box
          {...attributes}
          {...listeners}
          sx={{ cursor: disabled ? 'default' : 'grab', display: 'flex', color: 'text.disabled' }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>

        <Tooltip title={meta.label}>
          <Box sx={{ display: 'flex', color: 'text.secondary' }}>{meta.icon}</Box>
        </Tooltip>

        <Typography variant="body2" sx={{ minWidth: 34, color: 'text.secondary' }}>
          {number}
        </Typography>

        <TextField
          variant="standard"
          placeholder={isHeading ? 'Kapitelüberschrift' : 'Titel des Bausteins'}
          value={title}
          disabled={disabled}
          onChange={(e) => { setTitle(e.target.value); onPatch({ description: e.target.value }); }}
          fullWidth
          InputProps={{
            disableUnderline: true,
            sx: { fontWeight: isHeading ? 700 : 500, fontSize: isHeading ? '1rem' : '0.95rem' },
          }}
        />

        {isService && (
          <>
            <TextField
              variant="standard"
              type="number"
              value={days}
              disabled={disabled}
              onChange={(e) => { setDays(e.target.value); onPatch({ quantity: e.target.value }); }}
              inputProps={{ step: 0.5, min: 0, style: { textAlign: 'right' } }}
              InputProps={{
                endAdornment: <InputAdornment position="end">PT</InputAdornment>,
              }}
              sx={{ width: 92 }}
            />
            <Typography
              variant="body2"
              sx={{ width: 96, textAlign: 'right', color: 'text.secondary' }}
            >
              {formatEuro(item.total_price)}
            </Typography>
            <Tooltip title={item.is_optional ? 'Zählt nicht in die Gesamtsumme' : 'Als optional markieren'}>
              <Chip
                size="small"
                label="Optional"
                color={item.is_optional ? 'secondary' : 'default'}
                variant={item.is_optional ? 'filled' : 'outlined'}
                onClick={disabled ? undefined : () => onPatch({ is_optional: !item.is_optional })}
              />
            </Tooltip>
          </>
        )}

        <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        {!isHeading && (
          <IconButton size="small" onClick={() => setOpen((v) => !v)}>
            <ExpandMoreIcon
              fontSize="small"
              sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: '0.2s' }}
            />
          </IconButton>
        )}
      </Box>

      {!isHeading && (
        <Collapse in={open} unmountOnExit>
          <Box sx={{ px: 2, pb: 2 }}>
            <RichTextField
              initialValue={item.body_html}
              placeholder="Beschreiben Sie diesen Baustein …"
              onChange={(html) => onPatch({ body_html: html }, { slow: true })}
              onBlur={(html) => onPatch({ body_html: html }, { flush: true })}
            />
          </Box>
        </Collapse>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        <MenuItem onClick={() => { closeMenu(); onDuplicate(item); }}>
          <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} /> Duplizieren
        </MenuItem>
        {!isHeading && (
          <MenuItem onClick={() => { closeMenu(); onSaveAsTemplate(item); }}>
            <BookmarkAddIcon fontSize="small" sx={{ mr: 1 }} /> Als Vorlage speichern
          </MenuItem>
        )}
        <MenuItem onClick={() => { closeMenu(); onDelete(item); }} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Löschen
        </MenuItem>
      </Menu>
    </Card>
  );
}

export default SortableBlockCard;
